import { useState, useEffect } from "react";
import { supabase } from "..//lib/supabase";
import emailjs from "@emailjs/browser";

export default function Landing() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [typewriterText, setTypewriterText] = useState("");

  const fullText = "What if 20 minutes could change everything?";

  useEffect(() => {
    const timer = setTimeout(() => {
      let currentIndex = 0;
      const interval = setInterval(() => {
        if (currentIndex <= fullText.length) {
          setTypewriterText(fullText.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(interval);
        }
      }, 80);
      return () => clearInterval(interval);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    setSubmitting(true);

    const { error } = await supabase
      .from("registrations")
      .insert({ name: formData.name, email: formData.email });

    if (error) console.error("Registration failed:", error);

    // Notify Alex via EmailJS (name only — no email stored in notification)
    try {
      await emailjs.send(
        "service_j0ipyoc",
        "template_d1igceg",
        { name: formData.name },
        "s8T_p8eg35-u6r0tP"
      );
    } catch (emailError) {
      console.error("EmailJS notification failed:", emailError);
    }

    // Always show confirmation — don't expose DB errors to user
    setSubmitting(false);
    setFormData({ name: "", email: "" });
    window.location.href = "/?page=welcome";
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Reset submitted state after modal animates out
    setTimeout(() => setSubmitted(false), 400);
  };

  return (
    <>
      {/* Member Login Button */}
      <button
        className="member-login"
        onClick={() => alert("Member login coming soon")}
      >
        Member Login
      </button>

      {/* Main Container */}
      <div className="container">
        <div
          className="landing-bg"
          style={{ backgroundImage: "url(/loa_modal_bg.PNG)" }}
        ></div>

        <div className="opening-copy">
          <p className="line line-1">
            The city doesn't stop. The notifications don't stop. The demands
            don't stop.
          </p>
          <p className="line line-2">
            You're expected to perform - constantly, flawlessly, endlessly.
          </p>
          <p className="line line-3">
            But where do you go when you need to stop?
          </p>
        </div>

        <div className="hero-twenty">
          <div className="heartbeat-container">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 500 250"
              preserveAspectRatio="xMidYMid meet"
            >
              <path
                className="heartbeat-line"
                d="M 0,125 L 100,125 L 125,75 L 150,175 L 175,125 L 200,125 L 225,100 L 250,150 L 275,125 L 500,125"
              />
            </svg>
          </div>
          <div className="number-twenty">20</div>
        </div>

        <div className="tagline-container">
          <div className="tagline">
            {typewriterText}
            <span className="cursor">|</span>
          </div>
        </div>

        <div className="cta-section">
          <button className="cta-btn" onClick={() => setIsModalOpen(true)}>
            Register Your Interest
          </button>
        </div>
      </div>

      <footer className="footer">© 2026 LOA</footer>

      {/* Modal */}
      {isModalOpen && (
        <div
          className={`modal-overlay ${isModalOpen ? "active" : ""}`}
          onClick={handleModalClose}
        >
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div
              className="modal-bg"
              style={{
                backgroundImage: "url(/loa_modal_bg.PNG)",
                opacity: 0.5,
                backgroundPosition: "center 40%",
              }}
            ></div>

            <button className="modal-close" onClick={handleModalClose}>
              &times;
            </button>

            <div className="modal-content">
              {!submitted ? (
                /* ─── Registration form ─── */
                <>
                  <div className="modal-logo">LOA</div>
                  <h3 className="modal-title">
                    Join Our Exclusive Early Access Community
                  </h3>
                  <p className="modal-subtitle">
                    Be the first to experience the reset.
                  </p>
                  <form className="modal-form" onSubmit={handleSubmit}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Full Name"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                    <input
                      type="email"
                      className="form-input"
                      placeholder="Email Address"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                    <button
                      type="submit"
                      className="form-submit"
                      disabled={submitting}
                      style={{ opacity: submitting ? 0.6 : 1 }}
                    >
                      {submitting ? "Submitting..." : "Request Access"}
                    </button>
                  </form>
                </>
              ) : (
                /* ─── Confirmation state ─── */
                <div className="confirmation">
                  <div className="modal-logo">LOA</div>
                  <div className="confirmation-check">✓</div>
                  <h3 className="modal-title">You're on the list.</h3>
                  <p className="modal-subtitle">
                    We'll review your request and be in touch shortly with your
                    private access link.
                  </p>
                  <p className="confirmation-note">
                    Keep an eye on your inbox - and check your spam just in
                    case.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }

        body {
          font-family: 'Outfit', -apple-system, BlinkMacSystemFont, sans-serif;
          background: var(--deep);
          color: var(--cream);
          min-height: 100vh;
          overflow-x: hidden;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        .member-login {
          position: fixed;
          top: 32px;
          right: 32px;
          background: transparent;
          border: 1px solid rgba(250, 248, 243, 0.3);
          color: var(--cream);
          padding: 12px 28px;
          border-radius: 30px;
          font-size: 13px;
          font-weight: 400;
          letter-spacing: 0.5px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 100;
          backdrop-filter: blur(10px);
        }

        .member-login:hover {
          background: var(--cream);
          color: var(--deep);
          border-color: var(--cream);
          transform: translateY(-2px);
        }

        .container {
          max-width: 700px;
          margin: 0 auto;
          padding: 100px 40px;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          position: relative;
        }

        .landing-bg {
          position: fixed;
          inset: 0;
          background-size: cover;
          background-position: center;
          opacity: 0.08;
          z-index: 0;
        }

        .opening-copy {
          text-align: center;
          margin-bottom: 100px;
          position: relative;
          z-index: 1;
        }

        .opening-copy .line {
          font-family: 'Crimson Pro', serif;
          font-size: 20px;
          font-weight: 300;
          line-height: 1.7;
          letter-spacing: -0.2px;
          margin-bottom: 0.6em;
          color: rgba(250, 248, 243, 0.85);
          opacity: 0;
          transform: translateY(20px);
        }

        .line-1 { animation: fadeUpLine 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.5s forwards; }
        .line-2 { animation: fadeUpLine 1.2s cubic-bezier(0.4, 0, 0.2, 1) 1.3s forwards; }
        .line-3 {
          font-style: italic;
          font-weight: 400;
          color: var(--cream);
          margin-bottom: 0;
          animation: fadeUpLine 1.2s cubic-bezier(0.4, 0, 0.2, 1) 2.1s forwards;
        }

        @keyframes fadeUpLine { to { opacity: 1; transform: translateY(0); } }

        .hero-twenty {
          position: relative;
          margin: 80px 0 100px;
          opacity: 0;
          animation: fadeUp 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.6s forwards;
          z-index: 1;
        }

        .heartbeat-container {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 500px;
          height: 250px;
          z-index: 1;
          opacity: 0.7;
        }

        .heartbeat-line {
          stroke: var(--coral);
          stroke-width: 2.5;
          fill: none;
          stroke-dasharray: 1200;
          stroke-dashoffset: 1200;
          animation: drawLine 3.5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
          filter: drop-shadow(0 0 8px rgba(232, 84, 79, 0.3));
        }

        @keyframes drawLine {
          0% { stroke-dashoffset: 1200; opacity: 0.4; }
          50% { opacity: 0.8; }
          100% { stroke-dashoffset: 0; opacity: 0.4; }
        }

        .number-twenty {
          font-family: 'Outfit', sans-serif;
          font-size: 220px;
          font-weight: 700;
          color: var(--coral);
          position: relative;
          z-index: 2;
          line-height: 0.9;
          letter-spacing: -8px;
          text-shadow: 0 0 40px rgba(232, 84, 79, 0.2);
        }

        .tagline-container {
          margin-bottom: 140px;
          min-height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          z-index: 1;
        }

        .tagline {
          font-family: 'Crimson Pro', serif;
          font-size: 32px;
          font-weight: 300;
          font-style: italic;
          letter-spacing: -0.3px;
          text-align: center;
          color: var(--cream);
        }

        .cursor {
          opacity: 1;
          animation: blink 1s step-end infinite;
          margin-left: 2px;
        }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        .cta-section {
          width: 100%;
          text-align: center;
          position: relative;
          z-index: 1;
        }

        .cta-btn {
          background: var(--coral);
          color: var(--deep);
          border: none;
          padding: 18px 48px;
          font-family: 'Outfit', sans-serif;
          font-size: 15px;
          font-weight: 500;
          letter-spacing: 0.5px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 16px rgba(232, 84, 79, 0.25);
          animation: pulse 3s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { box-shadow: 0 4px 16px rgba(232, 84, 79, 0.25); }
          50% { box-shadow: 0 4px 24px rgba(232, 84, 79, 0.45); }
        }

        .cta-btn:hover {
          background: var(--coral-dark);
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(232, 84, 79, 0.35);
          animation: none;
        }

        .cta-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(232, 84, 79, 0.3);
        }

        .footer {
          position: fixed;
          bottom: 20px;
          left: 0;
          right: 0;
          text-align: center;
          font-family: 'Outfit', sans-serif;
          font-size: 11px;
          font-weight: 300;
          color: rgba(250, 248, 243, 0.4);
          z-index: 100;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(26, 26, 26, 0.85);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          visibility: hidden;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .modal-overlay.active { opacity: 1; visibility: visible; }

        .modal-container {
          position: relative;
          width: 90%;
          max-width: 500px;
          background: var(--deep);
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4);
          transform: scale(0.9) translateY(20px);
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .modal-overlay.active .modal-container { transform: scale(1) translateY(0); }

        .modal-bg {
          position: absolute;
          inset: 0;
          background-image: url('/loa_modal_bg.png');
          background-size: cover;
          background-position: center;
          opacity: 0.25;
          z-index: 1;
        }

        .modal-close {
          position: absolute;
          top: 20px;
          right: 20px;
          background: transparent;
          border: none;
          color: var(--cream);
          font-size: 32px;
          line-height: 1;
          cursor: pointer;
          z-index: 3;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .modal-close:hover {
          background: rgba(250, 248, 243, 0.1);
          transform: rotate(90deg);
        }

        .modal-content {
          position: relative;
          z-index: 2;
          padding: 60px 48px;
          text-align: center;
        }

        .modal-logo {
          font-family: 'Outfit', sans-serif;
          font-size: 32px;
          font-weight: 700;
          color: var(--coral);
          margin-bottom: 32px;
          letter-spacing: 2px;
        }

        .modal-title {
          font-family: 'Crimson Pro', serif;
          font-size: 28px;
          font-weight: 300;
          color: var(--cream);
          line-height: 1.3;
          margin-bottom: 12px;
          letter-spacing: -0.3px;
        }

        .modal-subtitle {
          font-family: 'Outfit', sans-serif;
          font-size: 15px;
          font-weight: 300;
          color: rgba(250, 248, 243, 0.6);
          margin-bottom: 40px;
        }

        .modal-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .form-input {
          background: rgba(250, 248, 243, 0.04);
          border: 1px solid rgba(250, 248, 243, 0.15);
          color: var(--cream);
          padding: 18px 24px;
          font-family: 'Outfit', sans-serif;
          font-size: 15px;
          font-weight: 300;
          letter-spacing: 0.3px;
          border-radius: 8px;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .form-input:focus {
          outline: none;
          border-color: var(--coral);
          background: rgba(250, 248, 243, 0.06);
          box-shadow: 0 0 0 3px rgba(232, 84, 79, 0.1);
        }

        .form-input::placeholder {
          color: rgba(250, 248, 243, 0.4);
          font-weight: 300;
        }

        .form-submit {
          background: var(--coral);
          color: var(--deep);
          border: none;
          padding: 18px 24px;
          font-family: 'Outfit', sans-serif;
          font-size: 15px;
          font-weight: 500;
          letter-spacing: 0.5px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 16px rgba(232, 84, 79, 0.25);
          margin-top: 8px;
        }

        .form-submit:hover {
          background: var(--coral-dark);
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(232, 84, 79, 0.35);
        }

        .form-submit:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(232, 84, 79, 0.3);
        }

        /* ─── Confirmation state ─── */
        .confirmation {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          animation: fadeUp 0.5s ease forwards;
        }

        .confirmation-check {
          font-size: 48px;
          color: var(--coral);
          margin: 8px 0 16px;
        }

        .confirmation-note {
          font-family: 'Outfit', sans-serif;
          font-size: 13px;
          font-weight: 300;
          color: rgba(250, 248, 243, 0.35);
          margin-top: 8px;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Mobile */
        @media (max-width: 768px) {
          .container { padding: 80px 24px; }
          .opening-copy { margin-bottom: 80px; }
          .opening-copy .line { font-size: 18px; }
          .hero-twenty { margin: 60px 0 80px; }
          .number-twenty { font-size: 160px; letter-spacing: -6px; }
          .heartbeat-container { width: 380px; height: 190px; }
          .tagline-container { margin-bottom: 100px; min-height: 60px; }
          .tagline { font-size: 24px; }
          .member-login { top: 20px; right: 20px; padding: 10px 22px; font-size: 12px; }
          .modal-container { width: 95%; max-width: 420px; }
          .modal-content { padding: 48px 32px; }
          .modal-title { font-size: 24px; }
        }

        @media (max-width: 480px) {
          .container { padding: 60px 20px; }
          .opening-copy .line { font-size: 17px; }
          .number-twenty { font-size: 130px; letter-spacing: -5px; }
          .heartbeat-container { width: 300px; height: 150px; }
          .tagline { font-size: 22px; }
        }
      `}</style>
    </>
  );
}
